import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AdvancedOptions = ({ options, onOptionsChange, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleOptionChange = (key, value) => {
    onOptionsChange({
      ...options,
      [key]: value
    });
  };

  const duplicateHandlingOptions = [
    { value: 'skip', label: 'Skip Duplicates', description: 'Skip songs that already exist in the destination' },
    { value: 'replace', label: 'Replace Existing', description: 'Replace existing songs with new versions' },
    { value: 'keep-both', label: 'Keep Both', description: 'Keep both original and new versions' }
  ];

  const matchSensitivityOptions = [
    { value: 'strict', label: 'Strict Matching', description: 'Only exact matches (90%+ similarity)' },
    { value: 'moderate', label: 'Moderate Matching', description: 'Include similar matches (70%+ similarity)' },
    { value: 'relaxed', label: 'Relaxed Matching', description: 'Include all potential matches (50%+ similarity)' }
  ];

  return (
    <div className={`bg-surface border border-border rounded-lg ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 lg:p-6 flex items-center justify-between hover:bg-surface-secondary transition-smooth"
      >
        <div className="flex items-center space-x-3">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Advanced Options</h3>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={20} 
          className="text-text-muted"
        />
      </button>

      {isExpanded && (
        <div className="px-4 lg:px-6 pb-4 lg:pb-6 border-t border-border">
          <div className="space-y-6 mt-4">
            {/* Duplicate Handling */}
            <div>
              <h4 className="text-sm font-medium text-text-primary mb-3">
                Duplicate Handling
              </h4>
              <div className="space-y-2">
                {duplicateHandlingOptions.map((option) => (
                  <label key={option.value} className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-background transition-smooth">
                    <input
                      type="radio"
                      name="duplicateHandling"
                      value={option.value}
                      checked={options.duplicateHandling === option.value}
                      onChange={(e) => handleOptionChange('duplicateHandling', e.target.value)}
                      className="mt-1 w-4 h-4 text-primary bg-background border-border focus:ring-primary focus:ring-2"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">
                        {option.label}
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        {option.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Match Sensitivity */}
            <div>
              <h4 className="text-sm font-medium text-text-primary mb-3">
                Match Sensitivity
              </h4>
              <div className="space-y-2">
                {matchSensitivityOptions.map((option) => (
                  <label key={option.value} className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-background transition-smooth">
                    <input
                      type="radio"
                      name="matchSensitivity"
                      value={option.value}
                      checked={options.matchSensitivity === option.value}
                      onChange={(e) => handleOptionChange('matchSensitivity', e.target.value)}
                      className="mt-1 w-4 h-4 text-primary bg-background border-border focus:ring-primary focus:ring-2"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">
                        {option.label}
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        {option.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Metadata Preservation */}
            <div>
              <h4 className="text-sm font-medium text-text-primary mb-3">
                Metadata Preservation
              </h4>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:bg-background transition-smooth">
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Preserve Playlist Description
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      Copy playlist description to the destination
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={options.preserveDescription}
                    onChange={(e) => handleOptionChange('preserveDescription', e.target.checked)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:bg-background transition-smooth">
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Preserve Track Order
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      Maintain the original track sequence
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={options.preserveOrder}
                    onChange={(e) => handleOptionChange('preserveOrder', e.target.checked)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:bg-background transition-smooth">
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Create Backup
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      Create a backup of the original playlist
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={options.createBackup}
                    onChange={(e) => handleOptionChange('createBackup', e.target.checked)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </label>
              </div>
            </div>

            {/* Manual Review Toggle */}
            <div className="pt-4 border-t border-border">
              <label className="flex items-center justify-between cursor-pointer p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    Enable Manual Review
                  </p>
                  <p className="text-xs text-text-muted mt-1">
                    Review and approve uncertain matches before sync begins
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={options.manualReview}
                  onChange={(e) => handleOptionChange('manualReview', e.target.checked)}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                />
              </label>
            </div>

            {/* Reset to Defaults */}
            <div className="pt-4 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOptionsChange({
                  duplicateHandling: 'skip',
                  matchSensitivity: 'moderate',
                  preserveDescription: true,
                  preserveOrder: true,
                  createBackup: false,
                  manualReview: false
                })}
                iconName="RotateCcw"
                iconSize={16}
                className="text-text-muted hover:text-text-primary"
              >
                Reset to Defaults
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedOptions;